import { useState } from "react";
import { Star, Building2, MessageSquare, ChevronDown, Plus, X } from "lucide-react";
import { useGetRecensioni, useCreateRecensione, useGetStrutture } from "@/lib/supabaseQueries";

type Recensione = {
  id: number;
  ospite: string;
  struttura: string;
  piattaforma: "Airbnb" | "Booking.com" | "Vrbo" | "Diretto" | "Google";
  voto: number;
  testo: string;
  risposta?: string;
  data: string;
};

const PIATTAFORME = ["Airbnb", "Booking.com", "Vrbo", "Diretto", "Google"];

const piattaformaColor: Record<Recensione["piattaforma"], string> = {
  "Airbnb":      "bg-rose-50 text-rose-600",
  "Booking.com": "bg-blue-50 text-blue-600",
  "Vrbo":        "bg-indigo-50 text-indigo-600",
  "Diretto":     "bg-gray-100 text-gray-600",
  "Google":      "bg-yellow-50 text-yellow-600",
};

function Stars({ voto }: { voto: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`h-4 w-4 ${i <= voto ? "fill-amber-400 text-amber-400" : "text-border"}`} />
      ))}
    </div>
  );
}

const inp = "w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";
const lbl = "block text-sm font-medium text-foreground mb-1.5";

export default function Recensioni() {
  const { data: rawRecensioni = [] } = useGetRecensioni();
  const createRecensione = useCreateRecensione();
  const { data: strutture = [] } = useGetStrutture();

  const [filtroPiattaforma, setFiltroPiattaforma] = useState("Tutte");
  const [filtroVoto, setFiltroVoto] = useState("Tutti");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nuova, setNuova] = useState({
    ospite: "", struttura: "", piattaforma: "Diretto",
    voto: 5, testo: "", data: new Date().toISOString().slice(0, 10),
  });
  const setN = (k: string, v: any) => setNuova(f => ({ ...f, [k]: v }));

  const recensioni = (rawRecensioni as any[]).filter((r: any) =>
    (filtroPiattaforma === "Tutte" || r.piattaforma === filtroPiattaforma) &&
    (filtroVoto === "Tutti" || r.voto === Number(filtroVoto))
  );

  const avg = recensioni.length > 0 ? (recensioni.reduce((a: number, r: any) => a + r.voto, 0) / recensioni.length).toFixed(1) : "0.0";
  const dist = [5, 4, 3, 2, 1].map(v => ({ voto: v, count: recensioni.filter((r: any) => r.voto === v).length }));

  const handleSaveRecensione = async () => {
    if (!nuova.ospite.trim() || !nuova.testo.trim()) return;
    setSaving(true);
    await createRecensione.mutateAsync(nuova);
    setSaving(false);
    setShowForm(false);
    setNuova({ ospite: "", struttura: "", piattaforma: "Diretto", voto: 5, testo: "", data: new Date().toISOString().slice(0, 10) });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-foreground">Recensioni</h1>
          <div className="flex items-center gap-1.5 mt-1">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="text-sm font-bold text-foreground">{avg}</span>
            <span className="text-sm text-muted-foreground">su {recensioni.length} recensioni</span>
          </div>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors">
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </div>

      {/* Form nuova recensione */}
      {showForm && (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Nuova recensione</h2>
            <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Nome ospite *</label><input className={inp} value={nuova.ospite} onChange={e => setN("ospite", e.target.value)} placeholder="Es. Marco Rossi" /></div>
            <div><label className={lbl}>Struttura</label>
              <select className={inp} value={nuova.struttura} onChange={e => setN("struttura", e.target.value)}>
                <option value="">— Seleziona —</option>
                {(strutture as any[]).map((s: any) => <option key={s.id} value={s.nome}>{s.nome}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Piattaforma</label>
              <select className={inp} value={nuova.piattaforma} onChange={e => setN("piattaforma", e.target.value)}>
                {PIATTAFORME.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Data</label><input type="date" className={inp} value={nuova.data} onChange={e => setN("data", e.target.value)} /></div>
          </div>
          <div>
            <label className={lbl}>Voto</label>
            <div className="flex gap-2">
              {[1,2,3,4,5].map(v => (
                <button key={v} type="button" onClick={() => setN("voto", v)}
                  className={`w-9 h-9 rounded-xl border text-sm font-bold transition-all ${
                    nuova.voto >= v ? "bg-amber-400 border-amber-400 text-white" : "border-border text-muted-foreground hover:border-amber-300"
                  }`}>{v}</button>
              ))}
            </div>
          </div>
          <div><label className={lbl}>Testo recensione *</label>
            <textarea className={inp + " resize-none"} rows={4} value={nuova.testo} onChange={e => setN("testo", e.target.value)} placeholder="Scrivi il testo della recensione..." />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted/40 transition-colors">Annulla</button>
            <button type="button" onClick={handleSaveRecensione} disabled={saving || !nuova.ospite.trim() || !nuova.testo.trim()}
              className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 transition-colors">
              {saving ? "Salvataggio..." : "Salva recensione"}
            </button>
          </div>
        </div>
      )}

      {/* Distribuzione voti */}
      <div className="bg-card border border-border rounded-2xl p-5 mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-4">Distribuzione voti</h2>
        <div className="space-y-2">
          {dist.map(d => (
            <div key={d.voto} className="flex items-center gap-3">
              <div className="flex items-center gap-0.5 w-20 flex-shrink-0">
                {[1,2,3,4,5].map(i => <Star key={i} className={`h-3 w-3 ${i <= d.voto ? "fill-amber-400 text-amber-400" : "text-border"}`} />)}
              </div>
              <div className="flex-1 bg-muted/40 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${recensioni.length ? (d.count / recensioni.length) * 100 : 0}%` }} />
              </div>
              <span className="text-xs text-muted-foreground w-4 text-right">{d.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative">
          <select value={filtroPiattaforma} onChange={e => setFiltroPiattaforma(e.target.value)}
            className="appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2 text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
            {["Tutte", "Airbnb", "Booking.com", "Vrbo", "Diretto", "Google"].map(p => <option key={p}>{p}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
        <div className="relative">
          <select value={filtroVoto} onChange={e => setFiltroVoto(e.target.value)}
            className="appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2 text-sm bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
            {["Tutti", "5", "4", "3", "2", "1"].map(v => <option key={v}>{v}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Cards */}
      <div className="space-y-4">
        {recensioni.map((r: any) => (
          <div key={r.id} className="bg-card border border-border rounded-2xl p-5 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-semibold text-foreground">{r.ospite}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Stars voto={r.voto} />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(piattaformaColor as any)[r.piattaforma] || "bg-gray-100 text-gray-600"}`}>{r.piattaforma}</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs text-muted-foreground">{r.data}</span>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <Building2 className="h-3.5 w-3.5" />{r.struttura}
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground italic mb-3">"{r.testo}"</p>
            {r.risposta ? (
              <div className="bg-muted/30 rounded-xl p-3 border-l-2 border-primary">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-1">
                  <MessageSquare className="h-3.5 w-3.5" /> Tua risposta
                </div>
                <p className="text-xs text-muted-foreground">{r.risposta}</p>
              </div>
            ) : (
              <button className="text-xs text-primary hover:underline flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> Rispondi
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
